<template>
    <on-window-resize :execute="onWindowResize">
        <div>
            <div
                class="relative pb-24 overflow-hidden text-white xs:pb-32 md:pb-48 xl:pb-64 bg-blue"
                :style="{
                    '-webkit-clip-path': `polygon(0 0, 100% 0, 100% ${angle}%, 0 100%)`,
                    'clip-path': `polygon(0 0, 100% 0, 100% ${angle}%, 0 100%)`
                }"
            >
                <div class="container relative z-10 max-w-xl mx-auto">
                    <div class="max-w-lg py-8 mx-auto lg:mb-6 md:text-center">
                        <router-link
                            :to="{ name: 'www.home.index' }"
                            class="inline-block h-5 mb-5 md:mb-6 xl:mb-10"
                        >
                            <base-svg
                                name="logo-rentora"
                                class="h-full fill-primary-white"
                            />
                        </router-link>

                        <h1 class="mb-4 text-4xl font-semibold leading-tight tracking-tight font-noto-sans xs:text-5xl lg:text-6xl">
                            Rent collection for HMOs
                        </h1>

                        <h2 class="hidden mb-8 text-2xl md:text-3xl lg:text-4xl md:mb-12">
                            Fast and reliable rent collection for HMOs with Rentora - no worries, no hassle.
                        </h2>

                        <h2 class="mb-8 text-xl xs:text-2xl md:text-3xl lg:text-4xl md:mb-12">
                            We could save you
                            <div class="relative inline-block px-2 -mx-1">
                                <div class="absolute inset-0 -mt-1 bg-skew bg-white-10a" />

                                <strong class="relative">£1,176</strong><sup>*</sup>
                            </div>
                            per year in letting fees, <strong><em>per property</em></strong> - same service, save thousands.
                        </h2>

                        <remarketings-store
                            v-slot="{
                                sid,
                                is_loading: is_saving,
                                has_loaded: has_saved,
                                error,
                                store
                            }"
                            @success="onRemarketingsStoreSuccess"
                        >
                            <base-form
                                @submit="store(form)"
                                class="flex flex-col items-start max-w-sm mx-auto mb-3 md:flex-row"
                            >
                                <form-item
                                    :sid="sid"
                                    name="email"
                                    class="mb-3! md:mb-0! md:mr-3 w-full md:w-auto"
                                    error-class="text-white! text-left"
                                >
                                    <input-text
                                        v-model="form.email"
                                        name="email"
                                        type="email"
                                        placeholder="you@email.co.uk"
                                        class="shadow h-14 md:text-lg md:px-6"
                                    />
                                </form-item>

                                <base-button
                                    :is-loading="is_saving || has_saved"
                                    :disabled="is_saving || has_saved"
                                    class="w-full shadow h-14 md:text-lg md:w-48 btn btn-blue-darkest md:pt-4"
                                >
                                    Get Started
                                </base-button>
                            </base-form>
                        </remarketings-store>

                        <p class="max-w-xs mx-auto text-xs text-center md:max-w-sm">
                            No setup or hidden fees. By entering your email, you agree to receive marketing emails from Rentora. * Avg. UK rent £886pcm + 8% &amp; VAT.
                        </p>
                    </div>
                </div>

                <svg
                    class="absolute top-0 z-0"
                    :style="{
                        left: '60%',
                    }"
                    width="787"
                    height="847"
                    viewBox="0 0 787 847"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M103.281 230L202.781 -168H400.719L301.219 230H103.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M417.281 638L516.781 240H714.719L615.219 638H417.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M460 -369H660L560 31H360L460 -369Z"
                        fill="#3E8ED2"
                    />

                    <path
                        d="M618 -169H818L718 231H518L618 -169Z"
                        fill="#3E8ED2"
                    />

                    <path
                        d="M157.281 846L256.781 448H454.719L355.219 846H157.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M358 39H558L458 439H258L358 39Z"
                        fill="#3E8ED2"
                    />

                    <path
                        d="M1.28077 638L100.781 240H298.719L199.219 638H1.28077Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />
                </svg>

                <svg
                    class="absolute bottom-0 z-0"
                    :style="{
                        right: '60%',
                    }"
                    width="716"
                    height="488"
                    viewBox="0 0 716 488"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1.28077 599L100.781 201H298.719L199.219 599H1.28077Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M259.281 399L358.781 1H556.719L457.219 399H259.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M417.281 599L516.781 201H714.719L615.219 599H417.281Z"
                        stroke="#3E8ED2"
                        stroke-width="2"
                    />

                    <path
                        d="M256 408H456L356 808H156L256 408Z"
                        fill="#3E8ED2"
                    />
                </svg>
            </div>

            <div class="container relative z-10 max-w-sm mx-auto -mt-24 lg:max-w-md xl:max-w-lg xs:-mt-32 md:-mt-48 xl:-mt-64">
                <img
                    src="/img/lp-online-rent-collection.png"
                    class="w-full"
                >
            </div>

            <div class="container max-w-2xl mx-auto">
                <grid-layout class="mt-6 mb-12 md:mt-12 lg:mb-24">
                    <grid-layout-item class="mb-8 lg:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-card"
                                class="w-8 h-8 fill-secondary-blue fill-primary-blue-lighter"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Easy, direct payments
                        </h6>

                        <p class="mb-3">
                            Your tenancy can pay you directly by <strong>credit or debit card</strong>.
                        </p>

                        <p class="mb-3">
                            Payments are processed quickly, and deposited <strong>directly into your bank account</strong> the next day.
                        </p>

                        <p class="mb-6">
                            <strong>We <em>never</em> touch your money.</strong>
                        </p>

                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 200 17"
                            :style="{ width: '200px', height: '17px' }"
                        >
                            <path
                                fill="#AAB7C4"
                                d="M20.7 1.99995L17.9 15h-3.4l2.8-13.00005h3.4zM34.8 10.4l1.8-4.90005 1 4.90005h-2.8zm3.8 4.6h3.1L39 1.99995h-2.9c-.3078.00292-.6072.10044-.8576.27933-.2505.17889-.4398.43049-.5424.72067L29.7 15h3.5l.7-1.9h4.3l.4 1.9zm-8.8-4.3c0-3.40005-4.7-3.60005-4.7-5.10005.054-.32499.2276-.61803.4866-.82157.259-.20354.5848-.30285.9134-.27843 1.134-.10288 2.2747.10452 3.3.6l.6-2.8c-.987-.39118-2.0384-.59466-3.1-.6-3.3 0-5.7 1.8-5.7 4.3 0 1.9 1.7 2.9 2.9 3.5C25.7 10.1 26.3 10.5 26.2 11.1c0 .9-1 1.3-2 1.3-1.2102-.0154-2.4033-.2881-3.5-.8l-.6 2.8c1.1758.4725 2.4328.7103 3.7.7 3.7.1 6-1.7 6-4.4zM15.9 1.99995L10.5 15H6.89998l-2.7-10.40005c-.02201-.23838-.10795-.4664-.24875-.66-.14081-.1936-.33125-.34562-.55125-.44-1.03882-.52961-2.15119-.9004-3.3000044-1.1l.1000004-.4H5.89998c.3649-.00113.71786.12992.99361.3689.27576.23899.45564.56975.50639.9311l1.4 7.50005L12.4 1.99995h3.5zM175.4 7.50002v-1.3c-.019-.25907-.13-.50277-.314-.68644-.183-.18366-.427-.29498-.686-.31356H173v2.3h-1.5v-6.5h3.7c.437-.060618.88.05185 1.235.31341.355.26156.594.6515.665 1.08659v.5c.006.30925-.076.61386-.235.87909-.159.26522-.389.48037-.665.62091.252.15281.453.37687.577.64386.125.26699.168.56492.123.85614v1.6h-1.5zm-2.5-3.8h1.6c.159.04134.328.02359.475-.04996.147-.07354.263-.1979.325-.35004.041-.15917.024-.32793-.05-.47502-.073-.14709-.198-.26254-.35-.32498h-2v1.2zm21.3 3.8l-2.4-4.4v4.4h-3l-.5-1.39999h-2.8l-.5 1.39999h-3.2s-2-.3-2-3.1c0-3.4 1.7-3.499996 2.4-3.499996h1.9V2.40002h-1.5c-.165.01725-.325.06748-.471.14774-.145.08025-.273.18889-.375.31946-.103.13058-.178.28043-.222.44066-.043.16024-.054.3276-.032.49214v.5c0 2.2 2.6 1.5 2.7 1.5l1.9-4.899996h2.1l2.3 5.899996v-5.8h2.1l2.4 4.3v-4.3h1.5v6.60001l-2.3-.10001zm-8-3h1.5l-.7-1.9-.8 1.9zm-22.7 3v-4.5l-1.9 4.5h-1.2l-1.9-4.5v4.5h-3l-.5-1.39999h-2.8l-.5 1.39999H150l2.5-6.599996h2.1l2.3 5.999996v-5.9h2.4l1.7 4.10001 1.7-4.10001h2.4v6.60001l-1.6-.10001zm-10.7-3h1.5l-.7-1.9-.8 1.9zm13.1 3v-6.5h4.7v1.5h-3.2v1h3.1v1.5h-3.1v1.10001h3.2v1.39999h-4.7zm11.8.10001V1.00002h1.5v6.60001h-1.5zM181 16v-1.3c.001-.1402-.028-.2789-.084-.4072-.057-.1284-.14-.2434-.243-.3377-.104-.0943-.226-.1658-.36-.2097-.133-.0439-.274-.0594-.413-.0454h-1.6V16h-1.6V9.50002h3.9c.231-.04747.47-.04529.701.00642.231.0517.448.15175.637.29353.189.14178.345.32203.46.52893.114.2069.183.4356.202.6711v.4c.006.3093-.076.6139-.235.8791-.159.2652-.389.4804-.665.6209.249.1621.453.3847.593.647.14.2623.211.5558.207.853V16H181zm-2.6-3.8h1.7c.159.0531.333.0407.483-.0343.15-.075.264-.2065.317-.3657.053-.1591.041-.3328-.034-.4828-.075-.15-.207-.2641-.366-.3172-.055-.0492-.126-.0764-.2-.0764-.074 0-.145.0272-.2.0764h-1.7v1.2zm-8.1 3.8h-1.4l-1.7-1.9-1.7 1.9h-6V9.50002h5.9L167.2 11.6l1.9-2.09998h5.1c.23-.03813.466-.02919.693.02629.227.05548.441.15634.627.29651.187.14017.344.31678.461.51908.116.2023.191.4262.219.6581v.4c0 1.9-.7 2.5-2.8 2.5h-1.6V16h-1.5zm-2-3.2l2 2.3v-4.5l-2 2.2zm-7.2 1.8h3.4l1.6-1.8-1.6-1.8h-3.4v1h3.3v1.5h-3.3v1.1zm10.8-2.4h1.7c.159.0531.333.0407.483-.0343.15-.075.264-.2065.317-.3657.053-.1591.041-.3328-.034-.4828-.075-.15-.207-.2641-.366-.3172-.055-.0492-.126-.0764-.2-.0764-.074 0-.145.0272-.2.0764h-1.7v1.2zM192 16h-2.9v-1.5h2.5s.9.1.9-.5-1.4-.5-1.4-.5c-.504.0778-1.018-.0467-1.43-.3465-.412-.2997-.689-.7503-.77-1.2535v-.3c-.033-.4892.126-.9721.443-1.3458.318-.37361.769-.60837 1.257-.65417h3.4V11h-2.5s-.9-.2-.9.5c0 .5 1.2.5 1.2.5s2.5-.2 2.5 1.8c.057.542-.099 1.085-.435 1.5142-.336.4292-.825.711-1.365.7858h-.4l-.1-.1zm-8.6 0V9.50002h5V11H185v1h3.3v1.5H185v1.1h3.4V16h-5zm14.2 0h-2.9v-1.5h2.5s.9.1.9-.5-1.4-.5-1.4-.5c-.257.0379-.519.0235-.77-.0421-.251-.0657-.486-.1814-.691-.3401-.206-.1587-.377-.3572-.504-.5836s-.207-.4761-.235-.7342v-.3c-.033-.4892.126-.9721.443-1.3458.318-.37361.769-.60837 1.257-.65418h3.4V11h-2.5s-.9-.2-.9.5c0 .5 1.2.5 1.2.5s2.5-.2 2.5 1.8c.057.542-.099 1.085-.435 1.5142-.336.4292-.825.711-1.365.7858h-.4c0-.1 0-.1-.1-.1zM89.2 5.99999c-.3528-.02245-.7053.04901-1.0215.20711-.3162.1581-.5848.39718-.7785.69289-.1807-.28486-.4325-.5177-.7306-.67553-.2981-.15783-.6323-.2352-.9694-.22447-.2894-.02476-.5803.02686-.8435.1497-.2632.12283-.4896.31259-.6565.5503v-.6h-1.1V11h1.1V8.29999c-.0056-.17222.0242-.34375.0876-.50399.0634-.16024.1589-.30578.2808-.42762.1218-.12184.2674-.2174.4276-.28076.1602-.06336.3318-.09319.504-.08763.7 0 1.1.5 1.1 1.3V11h1.1V8.29999c-.0056-.17222.0242-.34375.0876-.50399.0634-.16024.1589-.30578.2808-.42762.1218-.12184.2674-.2174.4276-.28076.1602-.06336.3318-.09319.504-.08763.7 0 1.1.5 1.1 1.3V11h1.1V7.99999c.016-.26683-.0247-.53402-.1196-.78394-.0948-.24992-.2416-.47688-.4306-.6659-.189-.18901-.416-.33578-.6659-.43062-.2499-.09483-.5171-.13558-.7839-.11954zm6.9.8c-.1764-.22756-.4044-.40977-.6653-.53152-.2609-.12174-.547-.1795-.8347-.16848-.3319.00182-.6598.07188-.9634.20581s-.5765.32887-.8016.57272c-.225.24384-.3976.53137-.5068.84472-.1093.31335-.1529.64582-.1282.97675-.0161.32901.0335.65792.1459.96754s.2854.59377.5088.83577c.2234.2421.4928.4372.7925.574.2996.1367.6235.2125.9528.2227.2877.011.5738-.0467.8347-.1685.2609-.1217.4889-.3039.6653-.5315v.4h1.1V6.09999h-1.1v.7zM94.7 10.2c-.2057.0004-.4093-.0414-.5981-.123-.1888-.08152-.3589-.20104-.4996-.35109-.1406-.15006-.249-.32745-.3182-.52114-.0692-.19369-.0978-.39955-.0841-.60478 0-.39782.158-.77935.4393-1.06066.2813-.2813.6629-.43934 1.0607-.43934.3978 0 .7793.15804 1.0606.43934.2813.28131.4394.66284.4394 1.06066.0137.20523-.0149.41109-.0842.60478-.0692.19369-.1775.37108-.3182.52114-.1407.15005-.3107.26957-.4995.35109-.1888.0816-.3924.1234-.5981.123zm6.2-2.10001l-.5-.1c-.4-.1-.8-.2-.8-.5s.3-.6.9-.6c.526-.00058 1.044.13737 1.5.4l.5-.8c-.61-.34121-1.301-.51379-2-.5-1.2 0-2 .6-2 1.6 0 .8.6 1.3 1.6 1.4l.5.1c.6.1.8.3.8.5 0 .4-.4.60001-1.1.60001-.287.0269-.5756-.0034-.8503-.0893-.2746-.0858-.5295-.22543-.7497-.41071l-.5.80001c.6254.4028 1.3561.6116 2.1.6 1.4 0 2.2-.7 2.2-1.60001 0-.9-.5-1.2-1.6-1.4zM106.3 10.2c-.113-.0022-.224-.0278-.327-.0751-.103-.0474-.195-.1154-.27-.19997-.075-.08453-.132-.18368-.167-.29123-.035-.10754-.047-.22116-.036-.33371v-2.1h1.9v-1h-1.9v-1.5h-1.1v1.5h-1v1h1v2.1C104.4 10.6 105 11.2 106.1 11.2c.541.0029 1.069-.1728 1.5-.5l-.4-.90001c-.257.21441-.569.35291-.9.40001zm4.4-4.20001c-.334-.00581-.666.05908-.973.19043-.307.13134-.583.32617-.81.57169-.226.24552-.399.53619-.505.85293-.106.31674-.144.6524-.112.98495-.03.34053.012.68363.126 1.00615.113.32251.295.61696.532.86336.237.2465.524.4392.842.5652.317.126.659.1824 1 .1653.729.0123 1.438-.2359 2-.7l-.5-.80001c-.402.31121-.892.48641-1.4.50001-.344.0222-.683-.0908-.944-.31504-.262-.22421-.425-.54181-.456-.88497h3.6v-.4c.016-.329-.034-.65792-.146-.96754-.112-.30962-.285-.59375-.509-.83579-.223-.24204-.493-.43715-.792-.57393-.3-.13679-.624-.21251-.953-.22274zm-1.3 2.2c.005-.16462.043-.32657.111-.4764.069-.14982.166-.28452.287-.39624.121-.11171.263-.19819.418-.2544.155-.0562.319-.08101.484-.07296.667 0 1.067.4 1.2 1.2h-2.5zm5.9-1.5v-.6h-1.1V11h1.1V8.29999c-.017-.16644.001-.3346.054-.49333.053-.15872.139-.30439.253-.42733.113-.12294.252-.22034.406-.28574.154-.0654.32-.09731.487-.0936.278.00664.551.07495.8.2l.3-1.1c-.257-.08729-.529-.12132-.8-.1-.29-.03345-.584.01427-.849.13785s-.49.31817-.651.56215zm5 .3c.447.0151.874.19288 1.2.5l.7-.7c-.224-.25617-.501-.46046-.812-.59862-.311-.13817-.648-.20689-.988-.20138-.344-.01112-.688.04853-1.008.17526-.32.12672-.612.31783-.855.56152-.244.24368-.435.53476-.562.85524-.126.32047-.186.66354-.175 1.00798-.019.34637.036.69281.16 1.01675.124.32395.315.61816.56.86346.245.2452.539.4361.863.5601.324.1241.671.1785 1.017.1597.34.0055.677-.0632.988-.2014.311-.1381.588-.3424.812-.5986l-.7-.70001c-.156.1598-.343.28644-.549.37231-.206.086-.428.1294-.651.1277-.2-.0028-.398-.0477-.58-.1318-.182-.0841-.344-.20553-.476-.35641-.132-.15088-.231-.32782-.29-.51936-.06-.19154-.078-.39338-.054-.59244-.024-.19905-.006-.4009.054-.59244.059-.19153.158-.36848.29-.51936.132-.15087.294-.2723.476-.35641.182-.08411.38-.12901.58-.13179zm6.4-.2c-.176-.22756-.404-.40977-.665-.53152-.261-.12174-.547-.1795-.835-.16848-.332.00182-.66.07188-.963.20581-.304.13393-.577.32887-.802.57272-.225.24384-.398.53137-.507.84472-.109.31335-.153.64582-.128.97675-.016.32901.033.65792.146.96754.112.30962.285.59377.509.83577.223.2421.493.4372.792.574.3.1367.624.2125.953.2227.288.011.574-.0467.835-.1685.261-.1217.489-.3039.665-.5315v.4h1.1V6.09999h-1.1v.7zM125.3 10.2c-.206.0004-.409-.0414-.598-.123-.189-.08152-.359-.20104-.5-.35109-.14-.15006-.249-.32745-.318-.52114-.069-.19369-.098-.39955-.084-.60478 0-.39782.158-.77935.439-1.06066.282-.2813.663-.43934 1.061-.43934.398 0 .779.15804 1.061.43934.281.28131.439.66284.439 1.06066.014.20523-.015.41109-.084.60478-.069.19369-.178.37108-.318.52114-.141.15005-.311.26957-.5.35109-.189.0816-.392.1234-.598.123zm5.1-3.50001v-.6h-1.1V11h1.1V8.29999c-.017-.16644.001-.3346.054-.49333.053-.15872.139-.30439.253-.42733.113-.12294.252-.22034.406-.28574.154-.0654.32-.09731.487-.0936.278.00664.551.07495.8.2l.3-1.1c-.257-.08729-.529-.12132-.8-.1-.29-.03345-.584.01427-.849.13785s-.49.31817-.651.56215zm6.4-3v3c-.176-.22756-.404-.40977-.665-.53152-.261-.12174-.547-.1795-.835-.16848-.332.00182-.66.07188-.963.20581-.304.13393-.577.32887-.802.57272-.225.24384-.398.53137-.507.84472-.109.31335-.153.64582-.128.97675-.016.32901.033.65792.146.96754.112.30962.285.59377.509.83577.223.2421.493.4372.792.574.3.1367.624.2125.953.2227.288.011.574-.0467.835-.1685.261-.1217.489-.3039.665-.5315v.5h1.1V3.69999h-1.1zM135.4 10.2c-.206.0004-.409-.0414-.598-.123-.189-.08152-.359-.20104-.5-.35109-.14-.15006-.249-.32745-.318-.52114-.069-.19369-.098-.39955-.084-.60478 0-.39782.158-.77935.439-1.06066.282-.2813.663-.43934 1.061-.43934.398 0 .779.15804 1.061.43934.281.28131.439.66284.439 1.06066.014.20523-.015.41109-.084.60478-.069.19369-.178.37108-.318.52114-.141.15005-.311.26957-.5.35109-.189.0816-.392.1234-.598.123zM63.2 8.59999c-.0012-1.12971.2496-2.24547.7339-3.26609.4844-1.02061 1.1902-1.92041 2.0661-2.63391-1.3145-1.02683-2.932-1.58944-4.6-1.6-.9826-.00827-1.9568.18083-2.8649.55608-.9081.37525-1.7316.92901-2.4217 1.62845-.6902.69944-1.2328 1.53032-1.5958 2.44339-.3631.91307-.5391 1.88973-.5176 2.87208-.0351 1.40711.3313 2.79501 1.0563 4.00141.725 1.2065 1.7786 2.1814 3.0375 2.8109 1.2589.6295 2.6711.8873 4.0712.7435 1.4002-.1439 2.7304-.6835 3.835-1.5558-.868-.7423-1.5669-1.6618-2.0499-2.6967-.4829-1.035-.7387-2.1613-.7501-3.30331z"
                            />
                            <path
                                fill="#AAB7C4"
                                d="M70.6 1c-1.668.01055-3.2855.57317-4.6 1.6.8744.71467 1.579 1.61475 2.0629 2.63514.4838 1.02039.7348 2.13556.7348 3.26486 0 1.1293-.251 2.2445-.7348 3.2649C67.579 12.7852 66.8744 13.6853 66 14.4c1.3145 1.0268 2.932 1.5894 4.6 1.6 1.9891 0 3.8968-.7902 5.3033-2.1967C77.3098 12.3968 78.1 10.4891 78.1 8.5c0-1.98912-.7902-3.89678-2.1967-5.3033C74.4968 1.79018 72.5891 1 70.6 1z"
                                opacity=".5"
                            />
                            <path
                                fill="#AAB7C4"
                                d="M61.7999 8.59995c.0012-1.12971-.2496-2.24547-.7339-3.26609-.4844-1.02061-1.1902-1.92041-2.066-2.63391-1.7 1.4-3.8 3.5-3.8 5.9C55.2 11 57.2 13.1 59 14.5c.8758-.7136 1.5816-1.6133 2.066-2.634.4843-1.0206.7351-2.13633.7339-3.26605z"
                                opacity=".75"
                            />
                        </svg>
                    </grid-layout-item>

                    <grid-layout-item class="mb-8 lg:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-notification"
                                class="w-8 h-8 fill-secondary-blue-lighter fill-primary-blue"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Unlimited property management
                        </h6>

                        <p class="mb-3">
                            Manage an <strong>unlimited number</strong> of tenancies and tenants with our <strong>end-to-end tools</strong>.
                        </p>

                        <p>
                            Perform and share inventories, store and share documents, logged instant messaging and more.
                        </p>
                    </grid-layout-item>

                    <grid-layout-item class="mb-8 lg:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-mood-happy"
                                class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Automatic reminders & chasing
                        </h6>

                        <p class="mb-3">
                            We <strong>remind your tenants</strong> of upcoming rent, chase late payments and track all payments.
                        </p>

                        <p class="mb-3">
                            You'll be kept in the loop with <strong>SMS alerts</strong>.
                        </p>

                        <p>
                            <strong>You don't have to do anything.</strong>
                        </p>
                    </grid-layout-item>

                    <grid-layout-item class="mb-8 md:mb-0 md:w-1/2 lg:w-1/4">
                        <div class="inline-block p-2 mb-1 rounded-full sm:p-3 bg-blue-lightest">
                            <base-svg
                                name="icon-refresh"
                                class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                            />
                        </div>

                        <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                            Switching Service
                        </h6>

                        <p class="mb-3">
                            We'll help you make the <strong>switch from your existing agent</strong> to Rentora.
                        </p>

                        <p>
                            If you're an agent, we'll help you switch from your existing letting software &amp; processes.
                        </p>
                    </grid-layout-item>
                </grid-layout>

                <div class="flex flex-col items-center h-px">
                    <div class="w-64 h-px border-t" />

                    <div class="p-4 -mt-8 bg-white">
                        <base-svg
                            name="icon-add-circle"
                            class="w-8 h-8 fill-primary-grey fill-secondary-grey-lighter"
                        />
                    </div>
                </div>

                <div class="py-10 lg:py-20">
                    <grid-layout class="justify-center">
                        <grid-layout-item class="md:w-1/2">
                            <ul>
                                <li
                                    v-for="(perk, index) in [
                                        'Manage <strong>unlimited</strong> tenancies and tenants',
                                        '<strong>Collect rent</strong> via <strong>online payments</strong>',
                                        'Tenants pay by <strong>credit/debit cards</strong>',
                                        'Create and collect <strong>fees</strong> (move-in costs, utilities, etc.)',
                                    ]"
                                    :key="index"
                                    class="flex mb-3 items-top"
                                >
                                    <base-svg
                                        name="icon-check-circle"
                                        class="flex-none w-6 h-6 mr-3 fill-primary-white fill-secondary-blue"
                                    />

                                    <span v-html="perk" />
                                </li>
                            </ul>
                        </grid-layout-item>

                        <grid-layout-item class="md:w-1/2">
                            <ul>
                                <li
                                    v-for="(perk, index) in [
                                        'Create <strong>inventories</strong>, shareable with tenants',
                                        'Tenants build <strong>credit history</strong> with rent payments',
                                        'Send/receive <strong>direct messages</strong>',
                                        'Upload and store <strong>documents</strong>, shareable with tenants',
                                    ]"
                                    :key="index"
                                    class="flex mb-3 items-top"
                                >
                                    <base-svg
                                        name="icon-check-circle"
                                        class="flex-none w-6 h-6 mr-3 fill-primary-white fill-secondary-blue"
                                    />

                                    <span v-html="perk" />
                                </li>
                            </ul>
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>

            <div
                class="py-16 bg-center bg-no-repeat lg:py-20 bg-blue-bg"
                id="payments"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 50px right 150px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-2xl mx-auto">
                    <div class="mx-auto mb-8 text-center lg:w-2/3">
                        <h2 class="mb-4 text-2xl font-semibold leading-tight tracking-tight font-noto-sans text-grey-darkest xs:text-3xl md:text-4xl lg:text-5xl">
                            The average letting cost in the UK is<br>
                            <span class="text-blue">£1,028 pcm<sup class="text-base">*</sup></span>
                        </h2>

                        <p class="text-lg lg:text-xl">
                            We could save you <strong>£1,176</strong> per year in letting fees, per property.<br>
                            Same service, <em>save thousands</em>.
                        </p>
                    </div>

                    <div class="max-w-xl mx-auto">
                        <p class="mb-2 text-xs text-center md:text-right text-grey-dark">
                            * Avg. UK rent £886pcm + 8% &amp; VAT.
                        </p>

                        <div class="p-8 mb-12 bg-white rounded shadow-md lg:flex lg:flex-grow">
                            <div class="lg:flex-grow">
                                <div class="flex flex-col mb-6 border-b lg:flex-row lg:border-b-0 lg:items-center lg:mb-12">
                                    <div class="flex-grow pb-6 lg:border-r lg:pb-0 lg:pr-6">
                                        <h4 class="mb-4 text-xl font-semibold leading-tight tracking-tight font-noto-sans text-grey-darkest">
                                            Credit and debit cards
                                        </h4>

                                        <p class="mb-6">
                                            A tenant can make a payment with their credit or debit card through Rentora, at any time. We accept all of the major card brands.
                                        </p>

                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 200 17"
                                            :style="{ width: '200px', height: '17px' }"
                                        >
                                            <path
                                                fill="#AAB7C4"
                                                d="M20.7 1.99995L17.9 15h-3.4l2.8-13.00005h3.4zM34.8 10.4l1.8-4.90005 1 4.90005h-2.8zm3.8 4.6h3.1L39 1.99995h-2.9c-.3078.00292-.6072.10044-.8576.27933-.2505.17889-.4398.43049-.5424.72067L29.7 15h3.5l.7-1.9h4.3l.4 1.9zm-8.8-4.3c0-3.40005-4.7-3.60005-4.7-5.10005.054-.32499.2276-.61803.4866-.82157.259-.20354.5848-.30285.9134-.27843 1.134-.10288 2.2747.10452 3.3.6l.6-2.8c-.987-.39118-2.0384-.59466-3.1-.6-3.3 0-5.7 1.8-5.7 4.3 0 1.9 1.7 2.9 2.9 3.5C25.7 10.1 26.3 10.5 26.2 11.1c0 .9-1 1.3-2 1.3-1.2102-.0154-2.4033-.2881-3.5-.8l-.6 2.8c1.1758.4725 2.4328.7103 3.7.7 3.7.1 6-1.7 6-4.4zM15.9 1.99995L10.5 15H6.89998l-2.7-10.40005c-.02201-.23838-.10795-.4664-.24875-.66-.14081-.1936-.33125-.34562-.55125-.44-1.03882-.52961-2.15119-.9004-3.3000044-1.1l.1000004-.4H5.89998c.3649-.00113.71786.12992.99361.3689.27576.23899.45564.56975.50639.9311l1.4 7.50005L12.4 1.99995h3.5zM175.4 7.50002v-1.3c-.019-.25907-.13-.50277-.314-.68644-.183-.18366-.427-.29498-.686-.31356H173v2.3h-1.5v-6.5h3.7c.437-.060618.88.05185 1.235.31341.355.26156.594.6515.665 1.08659v.5c.006.30925-.076.61386-.235.87909-.159.26522-.389.48037-.665.62091.252.15281.453.37687.577.64386.125.26699.168.56492.123.85614v1.6h-1.5zm-2.5-3.8h1.6c.159.04134.328.02359.475-.04996.147-.07354.263-.1979.325-.35004.041-.15917.024-.32793-.05-.47502-.073-.14709-.198-.26254-.35-.32498h-2v1.2zm21.3 3.8l-2.4-4.4v4.4h-3l-.5-1.39999h-2.8l-.5 1.39999h-3.2s-2-.3-2-3.1c0-3.4 1.7-3.499996 2.4-3.499996h1.9V2.40002h-1.5c-.165.01725-.325.06748-.471.14774-.145.08025-.273.18889-.375.31946-.103.13058-.178.28043-.222.44066-.043.16024-.054.3276-.032.49214v.5c0 2.2 2.6 1.5 2.7 1.5l1.9-4.899996h2.1l2.3 5.899996v-5.8h2.1l2.4 4.3v-4.3h1.5v6.60001l-2.3-.10001zm-8-3h1.5l-.7-1.9-.8 1.9zm-22.7 3v-4.5l-1.9 4.5h-1.2l-1.9-4.5v4.5h-3l-.5-1.39999h-2.8l-.5 1.39999H150l2.5-6.599996h2.1l2.3 5.999996v-5.9h2.4l1.7 4.10001 1.7-4.10001h2.4v6.60001l-1.6-.10001zm-10.7-3h1.5l-.7-1.9-.8 1.9zm13.1 3v-6.5h4.7v1.5h-3.2v1h3.1v1.5h-3.1v1.10001h3.2v1.39999h-4.7zm11.8.10001V1.00002h1.5v6.60001h-1.5zM181 16v-1.3c.001-.1402-.028-.2789-.084-.4072-.057-.1284-.14-.2434-.243-.3377-.104-.0943-.226-.1658-.36-.2097-.133-.0439-.274-.0594-.413-.0454h-1.6V16h-1.6V9.50002h3.9c.231-.04747.47-.04529.701.00642.231.0517.448.15175.637.29353.189.14178.345.32203.46.52893.114.2069.183.4356.202.6711v.4c.006.3093-.076.6139-.235.8791-.159.2652-.389.4804-.665.6209.249.1621.453.3847.593.647.14.2623.211.5558.207.853V16H181zm-2.6-3.8h1.7c.159.0531.333.0407.483-.0343.15-.075.264-.2065.317-.3657.053-.1591.041-.3328-.034-.4828-.075-.15-.207-.2641-.366-.3172-.055-.0492-.126-.0764-.2-.0764-.074 0-.145.0272-.2.0764h-1.7v1.2zm-8.1 3.8h-1.4l-1.7-1.9-1.7 1.9h-6V9.50002h5.9L167.2 11.6l1.9-2.09998h5.1c.23-.03813.466-.02919.693.02629.227.05548.441.15634.627.29651.187.14017.344.31678.461.51908.116.2023.191.4262.219.6581v.4c0 1.9-.7 2.5-2.8 2.5h-1.6V16h-1.5zm-2-3.2l2 2.3v-4.5l-2 2.2zm-7.2 1.8h3.4l1.6-1.8-1.6-1.8h-3.4v1h3.3v1.5h-3.3v1.1zm10.8-2.4h1.7c.159.0531.333.0407.483-.0343.15-.075.264-.2065.317-.3657.053-.1591.041-.3328-.034-.4828-.075-.15-.207-.2641-.366-.3172-.055-.0492-.126-.0764-.2-.0764-.074 0-.145.0272-.2.0764h-1.7v1.2zM192 16h-2.9v-1.5h2.5s.9.1.9-.5-1.4-.5-1.4-.5c-.504.0778-1.018-.0467-1.43-.3465-.412-.2997-.689-.7503-.77-1.2535v-.3c-.033-.4892.126-.9721.443-1.3458.318-.37361.769-.60837 1.257-.65417h3.4V11h-2.5s-.9-.2-.9.5c0 .5 1.2.5 1.2.5s2.5-.2 2.5 1.8c.057.542-.099 1.085-.435 1.5142-.336.4292-.825.711-1.365.7858h-.4l-.1-.1zm-8.6 0V9.50002h5V11H185v1h3.3v1.5H185v1.1h3.4V16h-5zm14.2 0h-2.9v-1.5h2.5s.9.1.9-.5-1.4-.5-1.4-.5c-.257.0379-.519.0235-.77-.0421-.251-.0657-.486-.1814-.691-.3401-.206-.1587-.377-.3572-.504-.5836s-.207-.4761-.235-.7342v-.3c-.033-.4892.126-.9721.443-1.3458.318-.37361.769-.60837 1.257-.65418h3.4V11h-2.5s-.9-.2-.9.5c0 .5 1.2.5 1.2.5s2.5-.2 2.5 1.8c.057.542-.099 1.085-.435 1.5142-.336.4292-.825.711-1.365.7858h-.4c0-.1 0-.1-.1-.1zM89.2 5.99999c-.3528-.02245-.7053.04901-1.0215.20711-.3162.1581-.5848.39718-.7785.69289-.1807-.28486-.4325-.5177-.7306-.67553-.2981-.15783-.6323-.2352-.9694-.22447-.2894-.02476-.5803.02686-.8435.1497-.2632.12283-.4896.31259-.6565.5503v-.6h-1.1V11h1.1V8.29999c-.0056-.17222.0242-.34375.0876-.50399.0634-.16024.1589-.30578.2808-.42762.1218-.12184.2674-.2174.4276-.28076.1602-.06336.3318-.09319.504-.08763.7 0 1.1.5 1.1 1.3V11h1.1V8.29999c-.0056-.17222.0242-.34375.0876-.50399.0634-.16024.1589-.30578.2808-.42762.1218-.12184.2674-.2174.4276-.28076.1602-.06336.3318-.09319.504-.08763.7 0 1.1.5 1.1 1.3V11h1.1V7.99999c.016-.26683-.0247-.53402-.1196-.78394-.0948-.24992-.2416-.47688-.4306-.6659-.189-.18901-.416-.33578-.6659-.43062-.2499-.09483-.5171-.13558-.7839-.11954zm6.9.8c-.1764-.22756-.4044-.40977-.6653-.53152-.2609-.12174-.547-.1795-.8347-.16848-.3319.00182-.6598.07188-.9634.20581s-.5765.32887-.8016.57272c-.225.24384-.3976.53137-.5068.84472-.1093.31335-.1529.64582-.1282.97675-.0161.32901.0335.65792.1459.96754s.2854.59377.5088.83577c.2234.2421.4928.4372.7925.574.2996.1367.6235.2125.9528.2227.2877.011.5738-.0467.8347-.1685.2609-.1217.4889-.3039.6653-.5315v.4h1.1V6.09999h-1.1v.7zM94.7 10.2c-.2057.0004-.4093-.0414-.5981-.123-.1888-.08152-.3589-.20104-.4996-.35109-.1406-.15006-.249-.32745-.3182-.52114-.0692-.19369-.0978-.39955-.0841-.60478 0-.39782.158-.77935.4393-1.06066.2813-.2813.6629-.43934 1.0607-.43934.3978 0 .7793.15804 1.0606.43934.2813.28131.4394.66284.4394 1.06066.0137.20523-.0149.41109-.0842.60478-.0692.19369-.1775.37108-.3182.52114-.1407.15005-.3107.26957-.4995.35109-.1888.0816-.3924.1234-.5981.123zm6.2-2.10001l-.5-.1c-.4-.1-.8-.2-.8-.5s.3-.6.9-.6c.526-.00058 1.044.13737 1.5.4l.5-.8c-.61-.34121-1.301-.51379-2-.5-1.2 0-2 .6-2 1.6 0 .8.6 1.3 1.6 1.4l.5.1c.6.1.8.3.8.5 0 .4-.4.60001-1.1.60001-.287.0269-.5756-.0034-.8503-.0893-.2746-.0858-.5295-.22543-.7497-.41071l-.5.80001c.6254.4028 1.3561.6116 2.1.6 1.4 0 2.2-.7 2.2-1.60001 0-.9-.5-1.2-1.6-1.4zM106.3 10.2c-.113-.0022-.224-.0278-.327-.0751-.103-.0474-.195-.1154-.27-.19997-.075-.08453-.132-.18368-.167-.29123-.035-.10754-.047-.22116-.036-.33371v-2.1h1.9v-1h-1.9v-1.5h-1.1v1.5h-1v1h1v2.1C104.4 10.6 105 11.2 106.1 11.2c.541.0029 1.069-.1728 1.5-.5l-.4-.90001c-.257.21441-.569.35291-.9.40001zm4.4-4.20001c-.334-.00581-.666.05908-.973.19043-.307.13134-.583.32617-.81.57169-.226.24552-.399.53619-.505.85293-.106.31674-.144.6524-.112.98495-.03.34053.012.68363.126 1.00615.113.32251.295.61696.532.86336.237.2465.524.4392.842.5652.317.126.659.1824 1 .1653.729.0123 1.438-.2359 2-.7l-.5-.80001c-.402.31121-.892.48641-1.4.50001-.344.0222-.683-.0908-.944-.31504-.262-.22421-.425-.54181-.456-.88497h3.6v-.4c.016-.329-.034-.65792-.146-.96754-.112-.30962-.285-.59375-.509-.83579-.223-.24204-.493-.43715-.792-.57393-.3-.13679-.624-.21251-.953-.22274zm-1.3 2.2c.005-.16462.043-.32657.111-.4764.069-.14982.166-.28452.287-.39624.121-.11171.263-.19819.418-.2544.155-.0562.319-.08101.484-.07296.667 0 1.067.4 1.2 1.2h-2.5zm5.9-1.5v-.6h-1.1V11h1.1V8.29999c-.017-.16644.001-.3346.054-.49333.053-.15872.139-.30439.253-.42733.113-.12294.252-.22034.406-.28574.154-.0654.32-.09731.487-.0936.278.00664.551.07495.8.2l.3-1.1c-.257-.08729-.529-.12132-.8-.1-.29-.03345-.584.01427-.849.13785s-.49.31817-.651.56215zm5 .3c.447.0151.874.19288 1.2.5l.7-.7c-.224-.25617-.501-.46046-.812-.59862-.311-.13817-.648-.20689-.988-.20138-.344-.01112-.688.04853-1.008.17526-.32.12672-.612.31783-.855.56152-.244.24368-.435.53476-.562.85524-.126.32047-.186.66354-.175 1.00798-.019.34637.036.69281.16 1.01675.124.32395.315.61816.56.86346.245.2452.539.4361.863.5601.324.1241.671.1785 1.017.1597.34.0055.677-.0632.988-.2014.311-.1381.588-.3424.812-.5986l-.7-.70001c-.156.1598-.343.28644-.549.37231-.206.086-.428.1294-.651.1277-.2-.0028-.398-.0477-.58-.1318-.182-.0841-.344-.20553-.476-.35641-.132-.15088-.231-.32782-.29-.51936-.06-.19154-.078-.39338-.054-.59244-.024-.19905-.006-.4009.054-.59244.059-.19153.158-.36848.29-.51936.132-.15087.294-.2723.476-.35641.182-.08411.38-.12901.58-.13179zm6.4-.2c-.176-.22756-.404-.40977-.665-.53152-.261-.12174-.547-.1795-.835-.16848-.332.00182-.66.07188-.963.20581-.304.13393-.577.32887-.802.57272-.225.24384-.398.53137-.507.84472-.109.31335-.153.64582-.128.97675-.016.32901.033.65792.146.96754.112.30962.285.59377.509.83577.223.2421.493.4372.792.574.3.1367.624.2125.953.2227.288.011.574-.0467.835-.1685.261-.1217.489-.3039.665-.5315v.4h1.1V6.09999h-1.1v.7zM125.3 10.2c-.206.0004-.409-.0414-.598-.123-.189-.08152-.359-.20104-.5-.35109-.14-.15006-.249-.32745-.318-.52114-.069-.19369-.098-.39955-.084-.60478 0-.39782.158-.77935.439-1.06066.282-.2813.663-.43934 1.061-.43934.398 0 .779.15804 1.061.43934.281.28131.439.66284.439 1.06066.014.20523-.015.41109-.084.60478-.069.19369-.178.37108-.318.52114-.141.15005-.311.26957-.5.35109-.189.0816-.392.1234-.598.123zm5.1-3.50001v-.6h-1.1V11h1.1V8.29999c-.017-.16644.001-.3346.054-.49333.053-.15872.139-.30439.253-.42733.113-.12294.252-.22034.406-.28574.154-.0654.32-.09731.487-.0936.278.00664.551.07495.8.2l.3-1.1c-.257-.08729-.529-.12132-.8-.1-.29-.03345-.584.01427-.849.13785s-.49.31817-.651.56215zm6.4-3v3c-.176-.22756-.404-.40977-.665-.53152-.261-.12174-.547-.1795-.835-.16848-.332.00182-.66.07188-.963.20581-.304.13393-.577.32887-.802.57272-.225.24384-.398.53137-.507.84472-.109.31335-.153.64582-.128.97675-.016.32901.033.65792.146.96754.112.30962.285.59377.509.83577.223.2421.493.4372.792.574.3.1367.624.2125.953.2227.288.011.574-.0467.835-.1685.261-.1217.489-.3039.665-.5315v.5h1.1V3.69999h-1.1zM135.4 10.2c-.206.0004-.409-.0414-.598-.123-.189-.08152-.359-.20104-.5-.35109-.14-.15006-.249-.32745-.318-.52114-.069-.19369-.098-.39955-.084-.60478 0-.39782.158-.77935.439-1.06066.282-.2813.663-.43934 1.061-.43934.398 0 .779.15804 1.061.43934.281.28131.439.66284.439 1.06066.014.20523-.015.41109-.084.60478-.069.19369-.178.37108-.318.52114-.141.15005-.311.26957-.5.35109-.189.0816-.392.1234-.598.123zM63.2 8.59999c-.0012-1.12971.2496-2.24547.7339-3.26609.4844-1.02061 1.1902-1.92041 2.0661-2.63391-1.3145-1.02683-2.932-1.58944-4.6-1.6-.9826-.00827-1.9568.18083-2.8649.55608-.9081.37525-1.7316.92901-2.4217 1.62845-.6902.69944-1.2328 1.53032-1.5958 2.44339-.3631.91307-.5391 1.88973-.5176 2.87208-.0351 1.40711.3313 2.79501 1.0563 4.00141.725 1.2065 1.7786 2.1814 3.0375 2.8109 1.2589.6295 2.6711.8873 4.0712.7435 1.4002-.1439 2.7304-.6835 3.835-1.5558-.868-.7423-1.5669-1.6618-2.0499-2.6967-.4829-1.035-.7387-2.1613-.7501-3.30331z"
                                            />
                                            <path
                                                fill="#AAB7C4"
                                                d="M70.6 1c-1.668.01055-3.2855.57317-4.6 1.6.8744.71467 1.579 1.61475 2.0629 2.63514.4838 1.02039.7348 2.13556.7348 3.26486 0 1.1293-.251 2.2445-.7348 3.2649C67.579 12.7852 66.8744 13.6853 66 14.4c1.3145 1.0268 2.932 1.5894 4.6 1.6 1.9891 0 3.8968-.7902 5.3033-2.1967C77.3098 12.3968 78.1 10.4891 78.1 8.5c0-1.98912-.7902-3.89678-2.1967-5.3033C74.4968 1.79018 72.5891 1 70.6 1z"
                                                opacity=".5"
                                            />
                                            <path
                                                fill="#AAB7C4"
                                                d="M61.7999 8.59995c.0012-1.12971-.2496-2.24547-.7339-3.26609-.4844-1.02061-1.1902-1.92041-2.066-2.63391-1.7 1.4-3.8 3.5-3.8 5.9C55.2 11 57.2 13.1 59 14.5c.8758-.7136 1.5816-1.6133 2.066-2.634.4843-1.0206.7351-2.13633.7339-3.26605z"
                                                opacity=".75"
                                            />
                                        </svg>
                                    </div>

                                    <div class="flex-none pb-6 lg:pb-0 lg:pl-6 lg:w-64 lg:text-center">
                                        <div class="text-4xl font-semibold font-noto-sans text-green">
                                            5%
                                        </div>

                                        per online payment
                                    </div>
                                </div>

                                <div class="flex flex-col lg:flex-row lg:items-center">
                                    <div class="flex-grow pb-6 lg:border-r lg:pb-0 lg:pr-6">
                                        <h4 class="mb-4 text-xl font-semibold leading-tight tracking-tight font-noto-sans text-grey-darkest">
                                            International payments
                                        </h4>

                                        <p>
                                            Additional fees are applied to cover the costs of currency conversion.
                                        </p>
                                    </div>

                                    <div class="flex-none lg:pl-6 lg:w-64 lg:text-center">
                                        If currency conversion is required, an additional <strong>2%</strong> fee will apply.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="text-center">
                        That's it! Everything else is <strong>free</strong>.
                    </div>
                </div>
            </div>

            <div class="container max-w-xl mx-auto">
                <div class="mt-12 mb-12 text-center">
                    <remarketings-store
                        v-slot="{
                            sid,
                            is_loading: is_saving,
                            has_loaded: has_saved,
                            error,
                            store
                        }"
                        @success="onRemarketingsStoreSuccess"
                    >
                        <base-form
                            @submit="store(form)"
                            class="flex flex-col items-start max-w-sm mx-auto mb-3 md:flex-row"
                        >
                            <form-item
                                :sid="sid"
                                name="email"
                                class="mb-3! md:mb-0! md:mr-3 w-full md:w-auto"
                                error-class="text-left"
                            >
                                <input-text
                                    v-model="form.email"
                                    name="email"
                                    type="email"
                                    placeholder="you@email.co.uk"
                                    class="h-14 md:text-lg md:px-6"
                                />
                            </form-item>

                            <base-button
                                :is-loading="is_saving || has_saved"
                                :disabled="is_saving || has_saved"
                                class="w-full h-14 md:text-lg md:w-48 btn btn-blue md:pt-4"
                            >
                                Get Started
                            </base-button>
                        </base-form>
                    </remarketings-store>
                </div>

                <div class="container max-w-xl mx-auto mb-6">
                    <div class="flex flex-col items-center justify-center text-xs">
                        <div class="flex-none px-3">
                            <router-link
                                :to="{ name: 'www.policies.terms.index' }"
                                class="underline"
                            >
                                Terms,
                            </router-link>

                            <router-link
                                :to="{ name: 'www.policies.privacy.index' }"
                                class="underline"
                            >
                                Privacy
                            </router-link>

                            &amp;

                            <router-link
                                :to="{ name: 'www.policies.cookies.index' }"
                                class="underline"
                            >
                                Cookie
                            </router-link>

                            policies
                        </div>

                        <div class="flex-none px-3">
                            &copy; {{ $moment().format('Y') }} Tenant Manager Ltd.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </on-window-resize>
</template>

<script>
    import _ from 'lodash'
    import local from '../../../store/local'
    import HeadMixin from '../../../mixins/head'
    import BaseButton from '../../../components/base-button'
    import RemarketingsStore from '../../../components/remarketings-store'

    export default {
        mixins: [
            HeadMixin
        ],
        components: {
            BaseButton,
            RemarketingsStore
        },
        data () {
            return {
                angle: 100,
                form: {
                    email: '',
                    campaign: 'guest-hmo'
                }
            }
        },
        watch: {
            'form.email' () {
                local.set('guest-email', this.form.email)
            }
        },
        created () {
            if (!this.form.email) {
                this.form.email = local.get('guest-email')
            }
        },
        methods: {
            head () {
                return {
                    title: [
                        'HMO rent collection',
                        'Easy, direct payments',
                        'Accept credit & debit cards'
                    ],
                    description: 'We could save you £1,176 per year in letting fees, per property - same service, save thousands.',
                    meta: [
                        {
                            property: 'og:image',
                            content: _.url('/img/head/og-pay-rent-with-credit-card.png')
                        },
                        {
                            name: 'twitter:image',
                            content: _.url('/img/head/twitter-pay-rent-with-credit-card.png')
                        }
                    ]
                }
            },
            onWindowResize () {
                switch (true) {
                    case this.$screen.is('lg'):
                        this.angle = 85
                        break
                    case this.$screen.is('md'):
                        this.angle = 90
                        break
                    case this.$screen.is('xs'):
                        this.angle = 94
                        break
                    default:
                        this.angle = 96
                }
            },
            onRemarketingsStoreSuccess () {
                this.$track(
                    'lp.hmo-rent-collection.index',
                    'form-submission',
                    'A guest is being remarketed to'
                )

                setTimeout(() => {
                    window.location = this.$laroute.route('auth.users.create', {
                        intended: this.$laroute.route('dashboard.landlord.tenancies.create', {
                            welcome: '✓'
                        })
                    })
                }, 0)
            }
        }
    }
</script>
